import { JsonObject } from '@/lib/helpers';
import { BaseModel } from '@/lib/models/base.model';
import { cloneDeep, difference, flatten, has, uniq } from 'lodash';
import { array, boolean, object, string } from 'yup';
import { PolicyAccessList, PolicyMeta, PolicyScope, PortalConfig } from '.';
import { syncInheritedControls } from './helpers';
import { PublicServiceConfig, syncPublicServiceAction } from './public-services';
import { ContentControlConfig } from './content-controls';
import {
  getSensitiveDataActionLevel,
  SensitiveDataConfig,
  SensitiveDataEntity,
  SensitiveDataEntityConfig,
} from './sensitive-data';
import { MetadataControlConfig } from './metadata-controls';
import { ClassifierControlConfig, RegexPatternConfig } from './classifier-controls';
import { McpControls } from './mcp-controls';
import { addProtocol } from '@/lib/url-helpers';
import { REQUIRED_MODEL_FAMILIES, RerouteConfig } from './reroute';
import { getControlActionLevel } from './controls';

export class PolicyModelV1 extends BaseModel {
  orgId: string;
  name: string;
  description: string;
  publicServices: PublicServiceConfig;
  contextDataSources: PolicyAccessList;
  assistants: PolicyAccessList;
  intents: PolicyAccessList;
  sensitiveData: SensitiveDataConfig;
  groups: string[];
  scope: PolicyScope;
  portal: PortalConfig;
  inheritFromDefaultPolicy?: boolean;
  sensitiveDataSources?: string[];
  contentControls: ContentControlConfig;
  mcpControls: McpControls;
  metadataControls: MetadataControlConfig;
  classifierControls: ClassifierControlConfig;
  regexPatterns: RegexPatternConfig;
  reroute: RerouteConfig;

  schema = object({
    orgId: string().required('Organization is required'),
    name: string().required('Name is required'),
    publicServices: object({
      enabled: array().of(string()).required('Enabled public service ids cannot be empty'),
      disabled: array().of(string()).required('Disabled public service ids cannot be empty'),
      monitored: array().of(string()).required('Monitored public service ids cannot be empty'),
      redirectUrl: string().when('blockAction', {
        is: 'redirect-url',
        then: (schema) =>
          schema
            .required('Redirect URL is required when the block action is set to "redirect"')
            .transform((value: string) => addProtocol(value))
            .url('Please enter a valid URL'),
        otherwise: (schema) => schema.optional(),
      }),
    }),
    contextDataSources: object({
      enabled: array().of(string()).required('Enabled data source ids cannot be empty'),
      disabled: array().of(string()).required('Disabled data source ids cannot be empty'),
    }),
    assistants: object({
      enabled: array().of(string()).required('Enabled assistant ids cannot be empty'),
      disabled: array().of(string()).required('Disabled assistant ids cannot be empty'),
    }),
    sensitiveData: object({
      action: string(),
      detect: boolean(),
      entities: array(),
    }),
    groups: array().of(string()),
    reroute: object({
      enabled: boolean(),
      modelFamilyReroutes: object().when('enabled', {
        is: true,
        then: (schema) =>
          schema.test('all-families-configured', (value, context) => {
            const entries = (value || {}) as Record<string, { privateModelId?: string }>;
            const missingFamilies = REQUIRED_MODEL_FAMILIES.filter(
              (family) => !entries[family]?.privateModelId
            );

            if (!missingFamilies.length) {
              return true;
            }

            return context.createError({
              message: `Model configuration required for: ${missingFamilies.join(', ')}`,
            });
          }),
      }),
    }),
  });

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof PolicyModelV1, unknown>;

    this.orgId = (data.orgId as string) || '';
    this.name = (data.name as string) || '';
    this.description = (data.description as string) || '';
    this.publicServices = (data.publicServices as PublicServiceConfig) || {
      enabled: [],
      disabled: [],
      monitored: [],
      allowPublicServicesByDefault: false,
      blockAction: 'block',
      redirectUrl: '',
      action: 'blocked',
    };

    this.contextDataSources = (data.contextDataSources as PolicyAccessList) || {
      enabled: [],
      disabled: [],
    };
    this.assistants = (data.assistants as PolicyAccessList) || {
      enabled: [],
      disabled: [],
    };
    this.intents = (data.intents as PolicyAccessList) || {
      enabled: [],
      disabled: [],
    };
    this.sensitiveData = (data.sensitiveData as SensitiveDataConfig) || {
      action: 'keep',
      detect: false,
      entities: [],
    };
    this.groups = (data.groups as string[]) || [];
    this.scope = (data.scope as PolicyScope) || 'group';
    this.portal = (data.portal as PortalConfig) || {
      enabled: false,
      defaultModelId: '',
      availablePrivateModelIds: [],
    };
    this.sensitiveDataSources = (data.sensitiveDataSources as string[]) || [];
    this.contentControls = (data.contentControls as ContentControlConfig) || { entities: [] };
    this.mcpControls = (data.mcpControls as McpControls) || {
      action: 'read-write',
      blockAction: 'log',
      services: {
        enabled: [],
        disabled: [],
      },
      tools: {
        enabled: [],
        disabled: [],
      },
    };
    this.reroute = (data.reroute as RerouteConfig) || {
      enabled: false,
      modelFamilyReroutes: {},
      applicationType: '',
    };
    this.metadataControls = (data.metadataControls as MetadataControlConfig) || {
      entities: [],
      forceDataPrivacy: this.scope === 'org',
    };
    this.classifierControls = (data.classifierControls as ClassifierControlConfig) || {
      entities: [],
    };
    this.regexPatterns = (data.regexPatterns as RegexPatternConfig) || {
      entities: [],
    };

    // legacy schema support for mcpControls.tools
    if (!has(this.mcpControls, 'tools')) {
      this.mcpControls.tools = {
        enabled: [],
        disabled: [],
      };
    }

    // legacy schema support for publicService.action
    if (!has(this, 'publicServices.action')) {
      this.publicServices.action = this.publicServices.allowPublicServicesByDefault
        ? 'protected'
        : 'blocked';
    }

    // legacy schema support for inheritFromDefaultPolicy
    if (this.scope === 'group') {
      this.inheritFromDefaultPolicy = true;
      if (has(data, 'inheritFromDefaultPolicy')) {
        this.inheritFromDefaultPolicy = !!data.inheritFromDefaultPolicy;
      }
    } else {
      this.inheritFromDefaultPolicy = false;
    }

    // legacy schema support for availablePrivateModelIds
    if (this.portal && !has(this, 'portal.availablePrivateModelIds')) {
      this.portal.availablePrivateModelIds = [];
      if (this.portal.defaultModelId) {
        this.portal.availablePrivateModelIds.push(this.portal.defaultModelId);
      }
    }

    // legacy schema support for allowPublicServicesByDefault and blockAction
    if (this.publicServices) {
      if (!has(this, 'publicServices.allowPublicServicesByDefault')) {
        this.publicServices.allowPublicServicesByDefault = false;
      }

      if (!has(this, 'publicServices.blockAction')) {
        this.publicServices.redirectUrl = '';
        this.publicServices.blockAction = this.portal.enabled ? 'redirect-portal' : 'block';
      }
    }

    /*
     * Legacy schema support for sensitive data entities. This value used to be an array of
     * strings containing entity type values that were enabled by the policy, it has since been
     * upgraded to a SensitiveDataEntity object with an enabled flag.
     */

    this.sensitiveData.entities = this.sensitiveData.entities.map(
      (entityVal: string | SensitiveDataEntity) => {
        const entityObj =
          typeof entityVal === 'string'
            ? { type: entityVal, enabled: true, scanType: 1 }
            : entityVal;

        if (!has(entityObj, 'enabled')) {
          entityObj.enabled = true;
        }

        return entityObj;
      }
    );
  }

  get meta(): PolicyMeta {
    return {
      id: this.id || '',
      name: this.name,
      description: this.description,
      scope: this.scope,
      groups: this.groups,
      publicServices: this.publicServices,
      contextDataSources: this.contextDataSources,
      portal: this.portal,
      assistants: this.assistants,
      isDefault: this.isDefault,
    };
  }

  get isDefault(): boolean {
    return this.scope === 'org';
  }

  /*
   * Merge an org policy into this group policy to create a single model which has
   * a composite view of settings for the given policy.
   */
  applyOrgPolicy(orgPolicy: PolicyModelV1) {
    if (orgPolicy.scope !== 'org' || this.scope !== 'group') {
      return;
    }

    this.portal.availablePrivateModelIds = flatten([
      this.portal.availablePrivateModelIds || [],
      orgPolicy.portal.availablePrivateModelIds || [],
    ]);

    if (!this.portal.defaultModelId && orgPolicy.portal.defaultModelId) {
      this.portal.defaultModelId = orgPolicy.portal.defaultModelId;
    }

    if (this.inherits()) {
      // Group policy cannot define a more restrictive action than the org policy
      if (
        getSensitiveDataActionLevel(this.sensitiveData.action) >
        getSensitiveDataActionLevel(orgPolicy.sensitiveData.action)
      ) {
        this.sensitiveData.action = orgPolicy.sensitiveData.action;
      }

      // Group policy sd detect cannot be enabled if its disabled by the org policy
      const orgDetectDisabled = orgPolicy.sensitiveData.detect;
      if (!orgDetectDisabled) {
        this.sensitiveData.detect = false;
      }

      // Group policies cannot define more-restrictive sd entity settings
      this.sensitiveData.entities = cloneDeep(orgPolicy.sensitiveData.entities).map(
        ({ type: orgType, enabled: orgEnabled, scanType: orgScanType }) => {
          if (!orgEnabled) {
            return {
              enabled: false,
              type: orgType,
              scanType: orgScanType,
            };
          }

          const { enabled: policyEnabled, scanType: policyScanType } =
            this.sensitiveData.entities.find(({ type }) => type === orgType) || {
              enabled: true,
              scanType: orgScanType,
            };

          return {
            type: orgType,
            enabled: policyEnabled,
            scanType: policyScanType,
          };
        }
      );
    }

    // ensure that group policy content controls are defined as not-more-restrictive than org policy
    if (this.inherits()) {
      // Sync content controls
      syncInheritedControls(
        this.contentControls,
        orgPolicy.contentControls,
        getControlActionLevel,
        'type'
      );

      // Sync metadata controls
      syncInheritedControls(
        this.metadataControls,
        orgPolicy.metadataControls,
        getControlActionLevel,
        'type'
      );

      // Sync classifier controls
      syncInheritedControls(
        this.classifierControls,
        orgPolicy.classifierControls,
        getControlActionLevel,
        'classifierId'
      );

      // Sync regex pattern controls
      syncInheritedControls(
        this.regexPatterns,
        orgPolicy.regexPatterns,
        getControlActionLevel,
        'classifierId'
      );
    }

    // ensure that the group policy lists public services available via default policy, but in read-only mode
    if (this.inherits()) {
      // ensure no service blocked by default policy is allowed here
      this.publicServices.enabled = difference(
        this.publicServices.enabled || [],
        orgPolicy.publicServices.disabled || []
      );

      // ensure no service blocked by default policy is allowed here
      this.publicServices.monitored = difference(
        this.publicServices.monitored || [],
        orgPolicy.publicServices.disabled || []
      );

      // @todo does this need to be called for default policy as well?
      // group policy cannot define a more-restrictive public service action
      syncPublicServiceAction(this, orgPolicy);
    }

    // group policies inherit enabled mcp services similar to public services
    if (this.inherits()) {
      this.mcpControls.services.enabled = uniq([
        ...this.mcpControls.services.enabled,
        ...(orgPolicy.mcpControls.services.enabled || []),
      ]);
    }

    // group policies inherit enabled mcp tools similar to public services
    if (this.inherits()) {
      this.mcpControls.tools.enabled = uniq([
        ...this.mcpControls.tools.enabled,
        ...(orgPolicy.mcpControls.tools.enabled || []),
      ]);

      this.mcpControls.tools.disabled = uniq([
        ...this.mcpControls.tools.disabled,
        ...(orgPolicy.mcpControls.tools.disabled || []),
      ]);
    }

    // group policies cannot define a more-restrictive mcp server action than the org policy
    if (this.inherits()) {
      if (orgPolicy.mcpControls.action === 'read-write' && this.mcpControls.action === 'read') {
        this.mcpControls.action = 'read-write';
      }
    }
  }

  /*
   * Inverse of applyOrgPolicy, for saving the group policy back to the database
   */
  stripOrgPolicy(orgPolicy: PolicyModelV1) {
    if (orgPolicy.scope !== 'org' || this.scope !== 'group') {
      return;
    }

    // ensure that no model enabled at the org level is enabled at the group level
    this.portal.availablePrivateModelIds = difference(
      this.portal.availablePrivateModelIds || [],
      orgPolicy.portal.availablePrivateModelIds || []
    );

    // group default model cannot be the same as the org default model
    if (this.portal.defaultModelId === orgPolicy.portal.defaultModelId) {
      this.portal.defaultModelId = '';
    }

    // ensure no service blocked by default policy is allowed here
    this.publicServices.enabled = difference(
      this.publicServices.enabled || [],
      orgPolicy.publicServices.disabled || []
    );

    // ensure no service blocked by default policy is allowed here
    this.publicServices.monitored = difference(
      this.publicServices.monitored || [],
      orgPolicy.publicServices.disabled || []
    );

    // remove enabled mcp services
    this.mcpControls.services.enabled = difference(
      this.mcpControls.services.enabled || [],
      orgPolicy.mcpControls.services.enabled || []
    );

    // remove mcp tools enabled by default policy
    this.mcpControls.tools.enabled = difference(
      this.mcpControls.tools.enabled || [],
      orgPolicy.mcpControls.tools.enabled || []
    );

    // remove mcp tools disabled by default policy
    this.mcpControls.tools.disabled = difference(
      this.mcpControls.tools.disabled || [],
      orgPolicy.mcpControls.tools.disabled || []
    );
  }

  entityEnabled(type: string): boolean {
    return this.sensitiveData.entities.some(({ type: enabledType }) => enabledType === type);
  }

  // Legacy support for org policies that are missing entities
  get allDisabledSensitiveDataEntityTypes(): Set<string> {
    const disabledTypes = (this.sensitiveData.entities || [])
      .filter(({ enabled }) => !enabled)
      .map(({ type }) => type);

    const supportedTypes = (this.sensitiveData.entities || []).map(({ type }) => type);

    const unsupportedTypes = SensitiveDataEntityConfig.filter(
      ({ entityType }) => !supportedTypes.includes(entityType)
    ).map(({ entityType }) => entityType);

    return new Set([...disabledTypes, ...unsupportedTypes]);
  }

  inherits(): boolean {
    return this.scope === 'group' && !!this.inheritFromDefaultPolicy;
  }

  get allowsPublicServices(): boolean {
    return ['protected', 'monitored'].includes(this.publicServices.action);
  }

  get newPublicServices(): string[] {
    const { enabled = [], monitored = [] } = this.publicServices;
    return monitored.filter((serviceId) => !enabled.includes(serviceId));
  }
}
